import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import CustomerNavbar from '../components/CustomerNavbar';
import Footer from '../components/Footer';
import LoadingSpinner from '../components/LoadingSpinner';
import { affiliateApi } from '../services/affiliateApi';

const AffiliateVerifyEmailPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const token = searchParams.get('token');
    if (!token) {
      setStatus('error');
      setMessage('Verification token is missing. Open the link from your email.');
      return;
    }

    let cancelled = false;
    affiliateApi
      .verifyEmail(token)
      .then((response) => {
        if (cancelled) return;
        setStatus('success');
        setMessage(response.message || 'Email verified. Your affiliate account is pending administrator approval.');
      })
      .catch((error: any) => {
        if (cancelled) return;
        setStatus('error');
        setMessage(error.response?.data?.message || 'Email verification failed. Please try again.');
      });

    return () => {
      cancelled = true;
    };
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-white flex flex-col no-animations">
      <CustomerNavbar showDashboardLink={false} showLogoutButton={false} showAuthButtons={true} />
      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full bg-white border border-gray-200 rounded-lg shadow-sm p-8 text-center">
          <div className="flex justify-center mb-6">
            {status === 'loading' && <LoadingSpinner />}
            {status === 'success' && <CheckCircle className="w-16 h-16 text-green-500" />}
            {status === 'error' && <XCircle className="w-16 h-16 text-red-500" />}
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-3">
            {status === 'loading' && 'Verifying your email'}
            {status === 'success' && 'Email verified'}
            {status === 'error' && 'Verification failed'}
          </h1>
          <p className="text-sm text-gray-600">{message}</p>
          {status === 'success' && (
            <div className="mt-4 bg-blue-50 border border-blue-200 rounded-lg p-4 text-left flex gap-3">
              <AlertCircle className="h-5 w-5 text-blue-700 flex-shrink-0" />
              <p className="text-sm text-blue-900">
                You can sign in now. The dashboard will show that your partner account is still pending administrator approval.
              </p>
            </div>
          )}
          {status !== 'loading' && (
            <Link to="/affiliates" className="aacore-btn-primary mt-6 justify-center">
              Go to affiliate sign in
            </Link>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default AffiliateVerifyEmailPage;
